import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { createQueryString, handleApiError } from './axiosConfig';
import { ScheduleItem, FilterInfo, Camp, MapPoint } from '../utils/types';
import { buildFilterParams } from '../utils/buildFilterParams';

// Get a single camp by ID
export const useGetCamp = (campId: string | undefined) => {
  return useQuery({
    queryKey: ['camp', campId],
    queryFn: async (): Promise<Camp> => {
      try {
        return await apiClient.get<Camp>(`/api/camps/${campId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch camp:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: !!campId,
  });
};

interface FindCampsByPromptRequest {
  prompt: string;
}

interface FindCampsResponse {
  camps: Camp[];
  filter_info?: FilterInfo;
}

export const useFilterCampsByPrompt = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: FindCampsByPromptRequest): Promise<FindCampsResponse> => {
      try {
        return await apiClient.post<FindCampsResponse>('/api/camps/query', data);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to filter camps by prompt:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['camps'] });
    },
    onError: (error) => {
      console.error('Camp filtering by prompt failed:', error);
    },
  });
};

// Filter camps mutation
export const useFilterCamps = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (filters: FilterInfo): Promise<FindCampsResponse> => {
      try {
        const queryParams = buildFilterParams(filters, null);
        const queryString = createQueryString(queryParams);
        const endpoint = queryString ? `/api/camps?${queryString}` : '/api/camps';

        return await apiClient.get<FindCampsResponse>(endpoint);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to filter camps:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['camps'] });
    },
    onError: (error) => {
      console.error('Camp filtering failed:', error);
    },
  });
};

// Map bounds parameters for bounded queries
export interface MapBoundsParams {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
  zoom: number | null;
}

// Response type for GET /api/camps with bounds
interface CampsWithBoundsResponse {
  camps: Camp[];
  pagination: {
    limit: number;
    next_cursor: string | null;
    has_more: boolean;
    total: number;
  };
  filter_info?: FilterInfo;
}

// Sort parameters for camps query
export interface SortParams {
  sort_by?: string;
  sort_order?: string;
}

// Fetch camps within map bounds with infinite scroll support
export const useGetCampsWithBounds = (
  filters: FilterInfo,
  bounds: MapBoundsParams | null,
  limit: number = 50,
  enabled: boolean = true,
  sort?: SortParams
) => {
  return useInfiniteQuery({
    queryKey: ['campsWithBounds', filters, bounds, limit, sort],
    queryFn: async ({ pageParam }): Promise<CampsWithBoundsResponse> => {
      try {
        const queryParams = {
          ...buildFilterParams(filters, bounds),
          limit,
          ...(pageParam && { cursor: pageParam }),
          ...(sort?.sort_by && { sort_by: sort.sort_by }),
          ...(sort?.sort_order && { sort_order: sort.sort_order }),
        };

        const queryString = createQueryString(queryParams);
        const endpoint = `/api/camps?${queryString}`;

        return await apiClient.get<CampsWithBoundsResponse>(endpoint);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch camps with bounds:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_cursor ?? undefined,
    enabled,
    staleTime: 10000, // Cache for 10 seconds
  });
};

// Response type for GET /api/map
interface MapPointsResponse {
  points: MapPoint[];
}

// Fetch map points for rendering markers
export const useGetMapPoints = (filters: FilterInfo, bounds: MapBoundsParams | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['mapPoints', filters, bounds],
    queryFn: async (): Promise<MapPointsResponse> => {
      try {
        const queryParams = buildFilterParams(filters, bounds);
        const queryString = createQueryString(queryParams);
        const endpoint = queryString ? `/api/map?${queryString}` : '/api/map';

        return await apiClient.get<MapPointsResponse>(endpoint);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch map points:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled,
    staleTime: 30000, // Cache for 30 seconds
  });
};
